import { isCardElementEditable } from '../../../../../../services/cards/components/AccountCard/helpers';
import {
  ORIENTATION,
  CARD_ELEMENT_TYPE,
} from '../../../../../../services/cards/constants';
import { hexToRGBA } from '../../../../../../util/hex-to-rgba';

const percentage = float => {
  if (!float) {
    return '0';
  }

  return `${float * 100}%`;
};

/**
 * Constructs the styles of a CardElement's frame.
 *
 * @param {Object} element The element UI state.
 * @param {Object} element.cardElement The CardElement.
 * @param {Object} element.cardElementConfiguration The CardElementConfiguration.
 * @returns {Object}
 */
export const getCardElementFrameStyle = element => {
  const { cardElement } = element;
  const isEditable = isCardElementEditable(element);

  const styles = {
    zIndex: cardElement.z_index,
    width: percentage(cardElement.frame.width),
    height: percentage(cardElement.frame.height),
    left: percentage(cardElement.frame.x),
    top: percentage(cardElement.frame.y),
    transform: `rotate(${cardElement.frame.rotation}rad)`,
    pointerEvents: isEditable ? 'auto' : 'none',
  };

  return styles;
};

/**
 * Constructs the styles of a CardElement's frame container.
 *
 * @param {Object} element The element UI state.
 * @param {Object} element.cardElement The CardElement.
 * @param {Object} element.cardElementConfiguration The CardElementConfiguration.
 * @param {Object} [elementBounds] The CardSide's size (width/height).
 * @param {Number} [elementBounds.width] The Element's width in px.
 * @param {Number} [elementBounds.height] The Element's width in px.
 * @returns {Object}
 */
export const getCardElementFrameContainerStyle = (element, elementBounds) => {
  const { cardElement, cardElementConfiguration = {} } = element;
  const { border } = cardElementConfiguration ?? {};
  const cornerRadius = cardElementConfiguration?.corner_radius ?? 0;
  const minPixelDimension = Math.min(elementBounds.width, elementBounds.height);

  const styles = {
    borderRadius: `${Math.ceil((minPixelDimension * cornerRadius) / 2)}px`,
  };

  if (border?.width && elementBounds?.width) {
    styles.borderStyle = border.type.toLowerCase();
    styles.borderColor = hexToRGBA(border.color, border.opacity);

    if (border.width) {
      styles.boxSizing = 'border-box';
      // Border width is based on the width of the element
      // Dive by 2, since the max border is half of the minimum size
      styles.borderWidth = `${Math.ceil(100 * border.width)}px`;
    }
  }

  // CardElementType specific styles
  if (cardElement.type === CARD_ELEMENT_TYPE.TEXT) {
    if (cardElementConfiguration.text_shadow) {
      const { text_shadow: shadow } = cardElementConfiguration;

      styles.textShadow = `${shadow.offset_x}px ${shadow.offset_y}px ${
        shadow.blur_radius
      }px ${hexToRGBA(shadow.color, shadow.opacity)}`;
    }

    if (typeof cardElementConfiguration.opacity !== 'undefined') {
      styles.opacity = cardElementConfiguration.opacity;
    }
  }

  if (cardElement.type === CARD_ELEMENT_TYPE.RECTANGLE) {
    if (cardElementConfiguration.color) {
      styles.color = cardElementConfiguration.color;
      styles.backgroundColor = cardElementConfiguration.color;
    }
  }

  if (cardElement.type === CARD_ELEMENT_TYPE.PHOTO) {
    if (cardElementConfiguration.background_color) {
      styles.backgroundColor = cardElementConfiguration.background_color;
    }

    if (
      cardElementConfiguration.asset_image?.url &&
      !cardElementConfiguration.cropbox
    ) {
      styles.backgroundImage = `url(${cardElementConfiguration.asset_image.url})`;
      styles.backgroundSize = 'cover';
      styles.backgroundRepeat = 'no-repeat';
      styles.backgroundPosition = 'center center';
    }

    if (cardElementConfiguration.box_shadow) {
      const { box_shadow: shadow } = cardElementConfiguration;

      styles.boxShadow = `${hexToRGBA(shadow.color, shadow.opacity)} ${
        shadow.offset_x
      }px ${shadow.offset_y}px ${shadow.blur_radius}px`;
    }

    if (typeof cardElementConfiguration.opacity !== 'undefined') {
      styles.opacity = cardElementConfiguration.opacity;
    }
  }

  return styles;
};

/**
 * Given the card & element bounds, determines the Inch based measurements for a photo element
 *
 * @param {Object} elementFrame An element containing the width, height of an element (in %)
 * @param {Object} cardPrintSize An element containing the width, height of the card print size (in inches)
 * @param {String} orientation The orientation, either Landscape or Portrait
 *
 * @returns {Object} an object containing the width, height of an element (in inches)
 */
export const getInchMeasurements = (
  elementFrame,
  cardPrintSize,
  orientation,
) => {
  if (!cardPrintSize || !elementFrame || !orientation) {
    return {};
  }
  const { width: cardInchWidth, height: cardInchHeight } = cardPrintSize;
  const { width: frameWidth, height: frameHeight } = elementFrame;

  const cardMaxSide = Math.max(cardInchWidth, cardInchHeight);
  const cardMinSide = Math.min(cardInchWidth, cardInchHeight);

  // The print size by itself is not enough, as it's the same for both orientations.
  // The proper way to handle this is to use the max / min based OFF the orientation.
  // eslint-disable-next-line no-use-before-define
  if (orientation === ORIENTATION.PORTRAIT) {
    return {
      width: Math.round(cardMinSide * frameWidth * 1e2) / 1e2,
      height: Math.round(cardMaxSide * frameHeight * 1e2) / 1e2,
      maxWidth: cardMinSide,
      maxHeight: cardMaxSide,
    };
  }

  return {
    width: Math.round(cardMaxSide * frameWidth * 1e2) / 1e2,
    height: Math.round(cardMinSide * frameHeight * 1e2) / 1e2,
    maxWidth: cardMaxSide,
    maxHeight: cardMinSide,
  };
};

/**
 * Given a width and height, determines the % based measurements for a photo element
 *
 * @param {Number} width width in inches
 * @param {Number} height height in inches
 * @param {Object} cardPrintSize An element containing the width, height of the card print size (in inches)
 * @param {String} orientation The orientation, either Landscape or Portrait
 *
 * @returns {Object} an object containing the width, height of an element (in inches)
 */
export const getPercentMeasurements = (
  width,
  height,
  cardPrintSize,
  orientation,
) => {
  if (!cardPrintSize || !orientation || !width || !height) {
    return {};
  }
  const { width: cardInchWidth, height: cardInchHeight } = cardPrintSize;

  const cardMaxSide = Math.max(cardInchWidth, cardInchHeight);
  const cardMinSide = Math.min(cardInchWidth, cardInchHeight);

  // The print size by itself is not enough, as it's the same for both orientations.
  // The proper way to handle this is to use the max / min based OFF the orientation.
  // eslint-disable-next-line no-use-before-define
  if (orientation === ORIENTATION.PORTRAIT) {
    return {
      width: Math.round((width / cardMinSide) * 1e3) / 1e3,
      height: Math.round((height / cardMaxSide) * 1e3) / 1e3,
    };
  }

  return {
    width: Math.round((width / cardMaxSide) * 1e3) / 1e3,
    height: Math.round((height / cardMinSide) * 1e3) / 1e3,
  };
};
